/**
 * useThirdPartyIframeTitler
 *
 * WCAG 4.1.2 + WCAG 2.5.3 safety net for third-party iframes.
 *
 * Our own iframes (report PDF embed, learn email preview, weekly
 * email preview) all carry an explicit `title` attribute. But the
 * page also loads two external scripts that inject iframes at
 * runtime, neither of which sets a title:
 *
 *   • Emergent platform badge (`assets.emergent.sh/scripts/emergent-main.js`)
 *   • PostHog session recording (`recordCrossOriginIframes: true`)
 *
 * Screen readers announce a missing-title iframe as just "frame",
 * which fails WCAG 4.1.2. AudioEye and axe-core both flag this.
 *
 * This hook installs a single MutationObserver on `document.body`
 * that gives every untitled iframe a meaningful, deduplicated
 * accessible name as soon as it gets attached. We also mark the
 * fixed iframe with `aria-hidden="true"` if it's clearly a tracking
 * widget (no visible UI for the user) so it stays out of the
 * accessibility tree entirely — the better outcome.
 */
import { useEffect } from "react";


const DECORATIVE_SRC_HINTS = [
    "posthog",
    "emergent.sh",
    "sentry",
    "doubleclick",
    "google-analytics",
    "googletagmanager",
];


function _ensureA11y(iframe) {
    if (!iframe || iframe.dataset.blA11yPatched === "1") return;
    iframe.dataset.blA11yPatched = "1";

    const src = (iframe.getAttribute("src") || "").toLowerCase();
    const isDecorative = DECORATIVE_SRC_HINTS.some((h) => src.includes(h));

    if (isDecorative) {
        // Pure analytics / branding widgets carry no information the
        // user needs to perceive — pull them out of the a11y tree.
        iframe.setAttribute("aria-hidden", "true");
        iframe.setAttribute("tabindex", "-1");
        if (!iframe.getAttribute("title")) {
            iframe.setAttribute("title", "Background widget");
        }
    } else if (!iframe.getAttribute("title")
            && !iframe.getAttribute("aria-label")
            && !iframe.getAttribute("aria-labelledby")) {
        // Unknown third-party iframe — fall back to a generic but
        // non-empty name so screen readers don't just announce "frame".
        iframe.setAttribute("title", "Embedded content");
    }
}


export default function useThirdPartyIframeTitler() {
    useEffect(() => {
        if (typeof document === "undefined") return undefined;

        // Sweep anything that's already in the DOM at mount.
        document.querySelectorAll("iframe").forEach(_ensureA11y);

        const obs = new MutationObserver((records) => {
            for (const r of records) {
                for (const node of r.addedNodes) {
                    if (!(node instanceof Element)) continue;
                    if (node.tagName === "IFRAME") {
                        _ensureA11y(node);
                    } else if (node.querySelectorAll) {
                        // The script might inject a container that
                        // holds the iframe nested inside.
                        node.querySelectorAll("iframe").forEach(_ensureA11y);
                    }
                }
            }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        return () => obs.disconnect();
    }, []);
}
